import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, User, Car, Calendar, Wrench, Check, ScanLine } from "lucide-react";
import { toast } from "sonner";
import ScanRegistrationDialog from "@/components/ScanRegistrationDialog";
import WorkItemRow, { computeTotals, autoCalcPrice, computeTotalWorkTimeAw } from "@/components/WorkItemRow";
import { usePricing } from "@/hooks/usePricing";
import { useTimeFormat } from "@/hooks/useTimeFormat";

const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUSES = ["new","planned","in_progress","waiting_parts","waiting_customer","finished","billed","cancelled"];

const emptyCustomer = { name: "", phone: "", email: "", address: "" };
const emptyVehicle = {
  brand: "", model: "", license_plate: "", vin: "", mileage: 0,
  first_registration: "", fuel_type: "", engine: "", notes: "",
};

function SectionHeader({ Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-zinc-300 mb-2">
      <Icon className="h-4 w-4 text-blue-400" />
      <span className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</span>
    </div>
  );
}

function Autocomplete({ value, onChange, options, placeholder, getLabel, getKey, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    if (!value) return options.slice(0, 8);
    const v = value.toLowerCase();
    return options.filter((o) => getLabel(o).toLowerCase().includes(v)).slice(0, 8);
  }, [value, options, getLabel]);
  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="bg-[#171B24] border-[#2E3241]"
        data-testid={testid}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full surface rounded-lg overflow-hidden shadow-lg max-h-56 overflow-y-auto">
          {filtered.map((o) => (
            <button
              type="button"
              key={getKey(o)}
              onClick={() => { onSelect(o); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-zinc-200 hover:bg-[#2A2F3A] flex items-center gap-2"
            >
              <Check className="h-3 w-3 text-blue-400" />
              {getLabel(o)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ContractDialog({ open, onOpenChange, initialEmployeeId, initialDate, onCreated }) {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [settings, setSettings] = useState(null);
  const hourlyRate = Number(settings?.hourly_rate ?? 100);
  const tf = useTimeFormat();
  const pricing = usePricing();

  const [customerMode, setCustomerMode] = useState("existing"); // existing | new
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState(emptyCustomer);
  const [absences, setAbsences] = useState([]);

  const [vehicleMode, setVehicleMode] = useState("existing");
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [newVehicle, setNewVehicle] = useState(emptyVehicle);

  const [planning, setPlanning] = useState({
    title: "",
    description: "",
    assigned_employee_id: initialEmployeeId || "",
    planned_date: initialDate || "",
    planned_time: "",
    priority: "normal",
    status: "new",
  });
  const [workItems, setWorkItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);

  const handleScanSaved = async (vehicle) => {
    // The scan dialog just created (or reused) a customer + a vehicle.
    // Refresh both lookups and auto-select them in this contract dialog.
    const [c, v] = await Promise.all([api.get("/customers"), api.get("/vehicles")]);
    setCustomers(c.data); setVehicles(v.data);
    const cust = c.data.find((x) => x.id === vehicle.customer_id);
    setCustomerMode("existing");
    setSelectedCustomerId(vehicle.customer_id);
    setCustomerQuery(cust?.name || "");
    setVehicleMode("existing");
    setSelectedVehicleId(vehicle.id);
    setVehicleQuery(`${vehicle.brand} ${vehicle.model} · ${vehicle.license_plate}`);
    toast.success(t("scan.assigned_to_contract", {
      defaultValue: "Vehicle & customer added — ready to book.",
    }));
  };

  useEffect(() => {
    if (!open) return;
    setPlanning((p) => ({
      ...p,
      assigned_employee_id: initialEmployeeId || p.assigned_employee_id,
      planned_date: initialDate || p.planned_date,
    }));
    Promise.all([
      api.get("/customers"), api.get("/vehicles"), api.get("/employees"), api.get("/settings"), api.get("/absences"),
    ]).then(([c, v, e, s, a]) => {
      setCustomers(c.data); setVehicles(v.data); setEmployees(e.data); setSettings(s.data); setAbsences(a.data);
    });
  }, [open, initialEmployeeId, initialDate]);

  // Reset when opening
  useEffect(() => {
    if (open) {
      setCustomerMode("existing"); setCustomerQuery(""); setSelectedCustomerId(""); setNewCustomer(emptyCustomer);
      setVehicleMode("existing"); setVehicleQuery(""); setSelectedVehicleId(""); setNewVehicle(emptyVehicle);
      setWorkItems([]);
      setPlanning({
        title: "", description: "",
        assigned_employee_id: initialEmployeeId || "",
        planned_date: initialDate || "",
        planned_time: "", priority: "normal", status: "new",
      });
    }
  }, [open, initialEmployeeId, initialDate]);

  const filteredVehicles = useMemo(() => {
    if (!selectedCustomerId) return vehicles;
    return vehicles.filter((v) => v.customer_id === selectedCustomerId);
  }, [vehicles, selectedCustomerId]);

  const totalAw = useMemo(() => computeTotalWorkTimeAw(workItems), [workItems]);
  const totals = useMemo(() => computeTotals(workItems, hourlyRate), [workItems, hourlyRate]);
  const totalPrice = totals.grand;

  const addWorkItem = () => {
    setWorkItems([...workItems, {
      id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `wi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      type: "labor",
      description: "",
      hours: 1,
      hourly_rate: hourlyRate,
      price: hourlyRate,
      price_manual: false,
      aw: 0,
      completed: false,
      notes: "",
      sub_items: [],
    }]);
  };

  const absenceConflict = useMemo(() => {
    const eid = planning.assigned_employee_id;
    const d = planning.planned_date;
    if (!eid || !d) return null;
    return absences.find((a) => a.employee_id === eid && a.start_date <= d && d <= a.end_date) || null;
  }, [planning.assigned_employee_id, planning.planned_date, absences]);

  const submit = async () => {
    setSubmitting(true);
    if (absenceConflict) {
      toast.error(t("absence.blocked_assignment"));
      setSubmitting(false);
      return;
    }
    try {
      // 1. Customer
      let customerId = selectedCustomerId;
      if (customerMode === "new") {
        if (!newCustomer.name.trim()) { toast.error(t("contracts.customer") + ": " + t("common.name")); setSubmitting(false); return; }
        const { data } = await api.post("/customers", newCustomer);
        customerId = data.id;
      }
      if (!customerId) { toast.error(t("contracts.customer")); setSubmitting(false); return; }

      // 2. Vehicle
      let vehicleId = selectedVehicleId;
      if (vehicleMode === "new") {
        if (!newVehicle.brand || !newVehicle.model || !newVehicle.license_plate) {
          toast.error(`${t("vehicles.brand")} / ${t("vehicles.model")} / ${t("vehicles.plate")}`);
          setSubmitting(false); return;
        }
        const { data } = await api.post("/vehicles", {
          ...newVehicle,
          customer_id: customerId,
          mileage: parseInt(newVehicle.mileage || 0, 10),
        });
        vehicleId = data.id;
      }
      if (!vehicleId) { toast.error(t("contracts.vehicle")); setSubmitting(false); return; }

      // 3. Contract
      const payload = {
        ...planning,
        customer_id: customerId,
        vehicle_id: vehicleId,
        assigned_employee_id: planning.assigned_employee_id || null,
        planned_date: planning.planned_date || null,
        planned_time: planning.planned_time || null,
        work_items: workItems.map((w) => ({
          ...w,
          type: w.type || "labor",
          aw: parseInt(w.aw || 0, 10),
          price: autoCalcPrice(w, hourlyRate),
          completed: false,
        })),
        checklist: [],
      };
      const { data } = await api.post("/contracts", payload);
      toast.success(data.number);
      onOpenChange(false);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#21252E] border-[#2E3241] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="font-display text-xl">{t("contracts.new_contract")}</DialogTitle>
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => setScanOpen(true)}
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 shrink-0"
              data-testid="contract-scan-registration-btn"
            >
              <ScanLine className="h-4 w-4 mr-1" /> {t("scan.button")}
            </Button>
          </div>
        </DialogHeader>

        {/* Customer */}
        <div className="surface rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <SectionHeader Icon={User} label={t("contracts.customer")} />
            <div className="flex gap-1">
              <Button type="button" size="sm" variant={customerMode === "existing" ? "default" : "ghost"}
                onClick={() => setCustomerMode("existing")}
                className={customerMode === "existing" ? "bg-blue-500 hover:bg-blue-600" : "text-zinc-400"}
                data-testid="customer-mode-existing">
                {t("common.search")}
              </Button>
              <Button type="button" size="sm" variant={customerMode === "new" ? "default" : "ghost"}
                onClick={() => setCustomerMode("new")}
                className={customerMode === "new" ? "bg-blue-500 hover:bg-blue-600" : "text-zinc-400"}
                data-testid="customer-mode-new">
                <Plus className="h-3 w-3 mr-1" /> {t("common.new")}
              </Button>
            </div>
          </div>

          {customerMode === "existing" ? (
            <Autocomplete
              value={customerQuery}
              onChange={(v) => { setCustomerQuery(v); setSelectedCustomerId(""); setSelectedVehicleId(""); }}
              options={customers}
              placeholder={t("common.name")}
              getLabel={(c) => `${c.name}${c.phone ? " · " + c.phone : ""}`}
              getKey={(c) => c.id}
              onSelect={(c) => { setSelectedCustomerId(c.id); setCustomerQuery(c.name); }}
              testid="contract-customer-search"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("common.name")}</Label>
                <Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" data-testid="new-customer-name" /></div>
              <div><Label className="text-xs">{t("common.phone")}</Label>
                <Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" /></div>
              <div><Label className="text-xs">{t("common.email")}</Label>
                <Input value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" /></div>
              <div><Label className="text-xs">{t("common.address")}</Label>
                <Input value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" /></div>
            </div>
          )}
        </div>

        {/* Vehicle */}
        <div className="surface rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <SectionHeader Icon={Car} label={t("contracts.vehicle")} />
            <div className="flex gap-1">
              <Button type="button" size="sm" variant={vehicleMode === "existing" ? "default" : "ghost"}
                onClick={() => setVehicleMode("existing")}
                className={vehicleMode === "existing" ? "bg-blue-500 hover:bg-blue-600" : "text-zinc-400"}
                data-testid="vehicle-mode-existing">
                {t("common.search")}
              </Button>
              <Button type="button" size="sm" variant={vehicleMode === "new" ? "default" : "ghost"}
                onClick={() => setVehicleMode("new")}
                className={vehicleMode === "new" ? "bg-blue-500 hover:bg-blue-600" : "text-zinc-400"}
                data-testid="vehicle-mode-new">
                <Plus className="h-3 w-3 mr-1" /> {t("common.new")}
              </Button>
            </div>
          </div>

          {vehicleMode === "existing" ? (
            <Autocomplete
              value={vehicleQuery}
              onChange={(v) => { setVehicleQuery(v); setSelectedVehicleId(""); }}
              options={filteredVehicles}
              placeholder={`${t("vehicles.brand")} / ${t("vehicles.plate")}`}
              getLabel={(v) => `${v.brand} ${v.model} · ${v.license_plate}`}
              getKey={(v) => v.id}
              onSelect={(v) => { setSelectedVehicleId(v.id); setVehicleQuery(`${v.brand} ${v.model} · ${v.license_plate}`); }}
              testid="contract-vehicle-search"
            />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {[
                ["brand", t("vehicles.brand")], ["model", t("vehicles.model")],
                ["license_plate", t("vehicles.plate")], ["vin", t("vehicles.vin")],
                ["mileage", t("vehicles.mileage")], ["first_registration", t("vehicles.first_registration")],
                ["fuel_type", t("vehicles.fuel")], ["engine", t("vehicles.engine")],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs">{l}</Label>
                  <Input value={newVehicle[k]} onChange={(e) => setNewVehicle({ ...newVehicle, [k]: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid={`new-vehicle-${k}`} />
                </div>
              ))}
              <div className="col-span-2">
                <Label className="text-xs">{t("common.notes")}</Label>
                <Textarea value={newVehicle.notes} onChange={(e) => setNewVehicle({ ...newVehicle, notes: e.target.value })}
                  className="bg-[#171B24] border-[#2E3241]" rows={2} />
              </div>
            </div>
          )}
        </div>

        {/* Planning */}
        <div className="surface rounded-lg p-4">
          <SectionHeader Icon={Calendar} label={t("planning.title")} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="col-span-2 md:col-span-3">
              <Label className="text-xs">{t("common.description")}</Label>
              <Input value={planning.title} onChange={(e) => setPlanning({ ...planning, title: e.target.value })}
                placeholder={t("contracts.title_placeholder")} className="bg-[#171B24] border-[#2E3241]" data-testid="contract-title-input" />
            </div>
            <div>
              <Label className="text-xs">{t("contracts.assigned_to")}</Label>
              <Select value={planning.assigned_employee_id} onValueChange={(v) => setPlanning({ ...planning, assigned_employee_id: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]" data-testid="contract-employee-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("contracts.plan_date")}</Label>
              <Input type="date" value={planning.planned_date} onChange={(e) => setPlanning({ ...planning, planned_date: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" data-testid="contract-date-input" />
            </div>
            <div>
              <Label className="text-xs">{t("contracts.plan_time")}</Label>
              <Input type="time" value={planning.planned_time} onChange={(e) => setPlanning({ ...planning, planned_time: e.target.value })}
                className="bg-[#171B24] border-[#2E3241]" />
            </div>
            <div>
              <Label className="text-xs">{t("common.priority")}</Label>
              <Select value={planning.priority} onValueChange={(v) => setPlanning({ ...planning, priority: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priorities.${p}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("common.status")}</Label>
              <Select value={planning.status} onValueChange={(v) => setPlanning({ ...planning, status: v })}>
                <SelectTrigger className="bg-[#171B24] border-[#2E3241]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#21252E] border-[#2E3241] text-white">
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`statuses.${s}`)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {absenceConflict && (
            <div className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
                 data-testid="absence-conflict-warning">
              {t("absence.blocked_assignment")} — {t(`absence.reasons.${absenceConflict.reason}`)} ({absenceConflict.start_date} → {absenceConflict.end_date})
            </div>
          )}
        </div>

        {/* Work items */}
        <div className="surface rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <SectionHeader Icon={Wrench} label={t("contracts.work_items")} />
            <div className="flex items-center gap-3 text-xs font-mono-stat">
              {totalAw > 0 && (
                <span className="text-zinc-400" data-testid="dialog-work-time">
                  {t("contracts.working_time")}: {tf.format(totalAw)}
                </span>
              )}
              {pricing.enabled && (
                <span className="text-emerald-300" data-testid="dialog-work-grand-total">{totalPrice.toFixed(2)} €</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            {workItems.length === 0 && (
              <p className="text-sm text-zinc-500">{t("common.empty")}</p>
            )}
            {workItems.map((w, idx) => (
              <WorkItemRow
                key={w.id || idx}
                item={w}
                idx={idx}
                disabled={false}
                defaultRate={hourlyRate}
                pricingEnabled={pricing.enabled}
                vatEnabled={pricing.enabled}
                defaultVat={pricing.defaultVat}
                vatOptions={pricing.vatOptions}
                onChange={(next) => setWorkItems(workItems.map((x, i) => i === idx ? {
                  ...next,
                  price: autoCalcPrice(next, hourlyRate),
                } : x))}
                onToggle={(v) => setWorkItems(workItems.map((x, i) => i === idx ? { ...x, completed: v } : x))}
                onDelete={() => setWorkItems(workItems.filter((_, i) => i !== idx))}
              />
            ))}
            <Button type="button" variant="ghost" onClick={addWorkItem}
              className="text-blue-400 hover:bg-[#2A2F3A]" data-testid="dialog-add-work-item">
              <Plus className="h-4 w-4 mr-1" /> {t("contracts.add_work_item")}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={submitting || !!absenceConflict} className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50" data-testid="dialog-submit-contract">
            {submitting ? "..." : t("common.create")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <ScanRegistrationDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        customers={customers}
        onSaved={handleScanSaved}
      />
    </Dialog>
  );
}
